import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'rizzui';
import { toast } from 'sonner';
import ContractTemplateWYSIWYGEditor from '../../../components/contract-templates/ContractTemplateWYSIWYGEditor';
import FormLabel from '../../../components/forms/FormLabel';
import CustomModal from '../../../components/modal/CustomModal';
import useFetch from '../../../hooks/useFetch';
import { TutorService } from '../../../services/tutor/tutorService';
import { TutorProvidingCorporation } from '../../../type/entities/tutor-type';
import { convertEditorJSONToHTML, convertHTMLToEditorJSON, isValidHTML } from '../../../utils/editorConversions';

interface AgreementModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onClose: () => void;
  tutorId: string;
  onSuccess?: () => void;
}

const parseDate = (value: any): Date | null => {
  if (!value) return null;
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'object' && typeof value.date === 'string') return new Date(value.date);
  return null;
};

const isAgreementSignedAndValid = (pc: TutorProvidingCorporation): boolean => {
  if (!pc?.collaborationSignature) return true;
  if (!pc?.hasAgreementSigned) return false;
  const signedDate = parseDate(pc.agreementSignedAt);
  if (!signedDate) return false;
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  return signedDate > oneYearAgo;
};

const AgreementModal: React.FC<AgreementModalProps> = ({
  isOpen,
  setIsOpen,
  onClose,
  tutorId,
  onSuccess,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const [variables, setVariables] = useState<any>([]);
  const [agreementContent, setAgreementContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [downloadingCorpId, setDownloadingCorpId] = useState<string | null>(null);
  const [tutorProvidingCorporations, setTutorProvidingCorporations] = useState<TutorProvidingCorporation[]>([]);

  const [templateData] = useFetch(useCallback(async () => {
    const response = await (new TutorService()).getAgreementTemplate();
    return response.getResponseData();
  }, []));

  const memoizedInitialData = useMemo(() => {
    if (!templateData) return undefined;
    try {
      return JSON.parse(templateData);
    } catch {
      if (isValidHTML(templateData)) return convertHTMLToEditorJSON(templateData);
      return undefined;
    }
  }, [templateData]);

  const loadTutorData = useCallback(async () => {
    if (!tutorId) return;
    try {
      const response = await (new TutorService()).getTutorById(tutorId);
      const payload = response.getResponseData();
      setTutorProvidingCorporations(payload?.data?.providingCorporations ?? []);
    } catch (error) {
      toast.error('No se pudo cargar la información del tutor.');
      setTutorProvidingCorporations([]);
    }
  }, [tutorId]);

  const loadTemplateFields = useCallback(async () => {
    try {
      const response = await (new TutorService()).getAgreementFields();
      const data = response.getResponseData()?.data;
      const formattedVariables = [
        {
          type: 'tutor',
          label: 'Datos del Tutor',
          variables: Object.entries(data || {}).map(([key, label]) => ({
            key: `{{${key}}}`,
            label: label as string,
          })),
        },
      ];
      setVariables(formattedVariables);
    } catch (error) {
      toast.error('Error al cargar las variables');
    }
  }, []);

  const resolveAgreementContent = (): string => {
    const trimmed = agreementContent.trim();
    if (trimmed.length > 0) return trimmed;
    if (typeof templateData === 'string' && isValidHTML(templateData)) return templateData;
    if (memoizedInitialData) return convertEditorJSONToHTML(memoizedInitialData);
    return '';
  };

  const sendAgreementSignatureSms = async () => {
    if (!tutorId) return;
    const contentToSend = resolveAgreementContent();

    if (!contentToSend) {
      toast.error('Debes definir el contenido del convenio antes de enviarlo.');
      return;
    }

    if (tutorProvidingCorporations.length === 0) {
      toast.error('El tutor no tiene impartidoras asignadas.');
      return;
    }

    setLoading(true);
    let sent = 0;
    let skipped = 0;
    let noRequired = 0;
    let failed = 0;

    for (const pc of tutorProvidingCorporations) {
      if (!pc.collaborationSignature) {
        noRequired++;
        continue;
      }

      if (isAgreementSignedAndValid(pc)) {
        skipped++;
        continue;
      }

      try {
        const response = await (new TutorService()).sendAgreementSignatureSms(
          tutorId,
          pc.providingCorporation.id,
          contentToSend
        );
        const payload = response.getResponseData();
        if (payload?.success) {
          sent++;
        } else {
          failed++;
        }
      } catch (error) {
        failed++;
      }
    }

    if (sent > 0) {
      toast.success(`SMS enviados: ${sent}${skipped > 0 ? ` | ya vigentes: ${skipped}` : ''}${noRequired > 0 ? ` | no requieren firma: ${noRequired}` : ''}${failed > 0 ? ` | errores: ${failed}` : ''}`);
    } else if (skipped > 0 && failed === 0) {
      toast.info('Todas las impartidoras ya tienen convenio firmado y vigente.');
    } else if (noRequired > 0 && failed === 0) {
      toast.info('No hay impartidoras pendientes de firma.');
    } else {
      toast.error('No se pudo enviar ningún SMS de firma.');
    }

    try {
      await loadTutorData();
      onSuccess?.();
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAgreement = async (pc: TutorProvidingCorporation) => {
    try {
      setDownloadingCorpId(pc.id);
      const response = (await (new TutorService()).downloadAgreementDocument(tutorId, pc.providingCorporation.id)).getResponseData();
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const corpName = (pc.providingCorporation?.name || 'impartidora').replace(/\s+/g, '-').toLowerCase();
      link.href = url;
      link.setAttribute('download', `convenio-${corpName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Hubo un problema al descargar el convenio.');
    } finally {
      setDownloadingCorpId(null);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    loadTemplateFields();
    loadTutorData();
  }, [isOpen, loadTemplateFields, loadTutorData]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key === 'Tab') {
        const focusableElements = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
          if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          } else if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
        onClose();
      }}
      onConfirm={sendAgreementSignatureSms}
      name='Convenios de Tutor'
      confirmText='Enviar SMS'
      cancelText='Cerrar'
      size="xl"
      isLoading={loading}
    >
      <div ref={modalRef} className="space-y-4">
        <div className="col-span-full">
          <FormLabel label="Plantilla de Convenio de Tutor" required />
          <ContractTemplateWYSIWYGEditor
            type='contract'
            initialData={memoizedInitialData}
            onChange={(editorData) => setAgreementContent(convertEditorJSONToHTML(editorData))}
            placeholder="Maqueta aquí la plantilla de convenio de tutor..."
            variables={variables}
          />
        </div>

        <div className="rounded-md border border-gray-200">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-medium text-gray-600 border-b border-gray-200">
            <div className="col-span-5">Impartidora</div>
            <div className="col-span-4">Estado convenio</div>
            <div className="col-span-3 text-right">Acciones</div>
          </div>
          {(tutorProvidingCorporations || []).map((pc) => {
            const isValid = isAgreementSignedAndValid(pc);
            const requiresSignature = !!pc.collaborationSignature;
            const isSigned = !!pc.hasAgreementSigned;
            const statusText = !requiresSignature ? 'No requiere firma' : (isValid ? 'Firmado y vigente' : (isSigned ? 'Firmado y caducado' : 'Pendiente'));
            return (
              <div key={pc.id} className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-b border-gray-100 last:border-b-0">
                <div className="col-span-5 text-sm">{pc.providingCorporation?.name || '-'}</div>
                <div className="col-span-4 text-sm">{statusText}</div>
                <div className="col-span-3 flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!requiresSignature || !isSigned}
                    isLoading={downloadingCorpId === pc.id}
                    onClick={() => handleDownloadAgreement(pc)}
                  >
                    Descargar
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </CustomModal>
  );
};

export default AgreementModal;
